// One scheme gallery, two origin groups.
//
// Built-ins and the tenant's own schemes used to live on separate tabs ("Themes" and
// "Custom"). They are the same kind of thing to someone picking one, so they share a grid and
// origin survives as a pill. The swatch strip identifies a scheme faster than its name does,
// which is why the pills only need to carry origin and live state.

import { Check, Edit, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SchemeCard } from './brandingHelpers';

interface SchemeGalleryProps {
  builtIn: SchemeCard[];
  yours: SchemeCard[];
  /** Highlighted card — the tenant's live scheme. */
  selectedId: string | null;
  onSelect: (card: SchemeCard) => void;
  canEdit: boolean;
  onCreate?: () => void;
  onEdit?: (card: SchemeCard) => void;
  onDelete?: (card: SchemeCard) => void;
}

function Pill({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'mine' | 'inuse';
}) {
  const tones: Record<string, string> = {
    neutral: 'border-border bg-muted/50 text-muted-foreground',
    mine: 'border-primary/30 bg-primary/10 text-primary',
    inuse: 'border-foreground bg-foreground text-background',
  };
  return (
    <span
      className={`whitespace-nowrap rounded-sm border px-1.5 py-0.5 font-mono text-[0.55rem] uppercase tracking-wider ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function SchemeTile({
  card,
  selected,
  canEdit,
  onSelect,
  onEdit,
  onDelete,
}: {
  card: SchemeCard;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [primary, secondary, accent, sidebar] = card.swatches;
  const isCustom = card.origin === 'custom';

  return (
    <div
      role="button"
      tabIndex={canEdit ? 0 : -1}
      aria-pressed={selected}
      aria-label={`${card.name} color scheme`}
      onClick={() => canEdit && onSelect()}
      onKeyDown={(e) => {
        if (!canEdit) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`overflow-hidden rounded-md border bg-card transition-all ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:shadow-md'
      } ${isCustom ? 'border-dashed' : ''} ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {/* Primary is rendered wide: it is the color a viewer will actually notice. */}
      <div className="flex h-8" aria-hidden="true">
        <span className="block flex-[2.2]" style={{ backgroundColor: primary }} />
        <span className="block flex-1" style={{ backgroundColor: secondary }} />
        <span className="block flex-1" style={{ backgroundColor: accent }} />
        <span className="block flex-1" style={{ backgroundColor: sidebar }} />
      </div>

      <div className="flex flex-col gap-1.5 px-2 pb-2 pt-1.5">
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs font-semibold leading-tight text-foreground">{card.name}</span>
          <div className="flex shrink-0 items-center">
            {selected && <Check className="h-3.5 w-3.5 text-primary" />}
            {canEdit && isCustom && onEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                aria-label={`Edit ${card.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
            {canEdit && isCustom && onDelete && (
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                aria-label={`Delete ${card.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1">
          <Pill tone={isCustom ? 'mine' : 'neutral'}>{isCustom ? 'Yours' : 'Built in'}</Pill>
          {card.isActive && <Pill tone="inuse">In use</Pill>}
        </div>
      </div>
    </div>
  );
}

function GroupHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-2">
      <h4 className="font-mono text-[0.65rem] uppercase tracking-wider text-muted-foreground">{title}</h4>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </div>
  );
}

export function SchemeGallery({
  builtIn,
  yours,
  selectedId,
  onSelect,
  canEdit,
  onCreate,
  onEdit,
  onDelete,
}: SchemeGalleryProps) {
  const grid = 'grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(9.5rem,1fr))]';

  return (
    <div className="space-y-4">
      <div>
        <GroupHeading title="Built in" hint="Maintained by Joules. Duplicate one to start your own." />
        <div className={grid}>
          {builtIn.map((card) => (
            <SchemeTile
              key={card.id}
              card={card}
              selected={card.id === selectedId}
              canEdit={canEdit}
              onSelect={() => onSelect(card)}
            />
          ))}
        </div>
      </div>

      <div>
        <GroupHeading title="Yours" hint="Created here, and editable." />
        <div className={grid}>
          {yours.map((card) => (
            <SchemeTile
              key={card.id}
              card={card}
              selected={card.id === selectedId}
              canEdit={canEdit}
              onSelect={() => onSelect(card)}
              onEdit={onEdit ? () => onEdit(card) : undefined}
              onDelete={onDelete ? () => onDelete(card) : undefined}
            />
          ))}

          {canEdit && onCreate && (
            <button
              type="button"
              onClick={onCreate}
              className="flex min-h-[5.4rem] flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border p-2 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
            >
              <Plus className="h-5 w-5 text-primary" />
              Create scheme
            </button>
          )}

          {yours.length === 0 && !canEdit && (
            <p className="text-xs text-muted-foreground">No custom schemes yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
